import {DataStatus} from 'platform/components';

import {isNotNil, toNumber} from 'ramda-adjunct';

import {useLazyCalculateFromWithoutVatQuery} from '@dms/api/core';
import {
  GetVehicleCostApiResponse,
  OneTimePrice,
  RecurringPriceCalculated,
  RecurringPriceSimple,
  useGetActiveSaleVehicleQuery,
  useGetVehicleCostQuery,
  useListVehicleCostCategoriesQuery,
  useUpdateVehicleCostMutation,
} from '@dms/api/vehicle';

import {DeepNonNullableWithPartial, Nullish, RequiredTestIdProps} from 'shared/web';

import {useSaleVehicleActions} from '../../hooks/useSaleVehicleActions';
import {useTenant} from '../../hooks/useTenant';
import {handleApiError} from '../../utils/handleApiError';
import {useSubscribeToFormChanges} from '../VehicleTransactionForm/hooks/useSubscribeToFormChanges';
import {
  VehicleSimplifiedTransactionForm,
  VehicleSimplifiedTransactionFormRef,
} from '../VehicleTransactionForm/VehicleSimplifiedTransactionForm';
import {
  VehicleTransactionForm,
  VehicleTransactionFormRef,
} from '../VehicleTransactionForm/VehicleTransactionForm';
import {
  CalculationType,
  TransactionType,
  VatCodePrice,
} from '../VehicleTransactionForm/VehicleTransactionFormSchema';

function getTransactionType(cost: GetVehicleCostApiResponse | Nullish): [TransactionType] {
  if (cost?.realPriceRecurringSimple || cost?.realPriceRecurringCalculated) {
    return ['RECURRING'] as const;
  }
  return ['ONE_TIME'] as const;
}

function getCalculationType(cost: GetVehicleCostApiResponse | Nullish): [CalculationType] {
  if (cost?.realPriceRecurringCalculated) {
    return ['CALCULATED'];
  }
  return ['SIMPLE'];
}

type FullFormSubmitHandler = Parameters<typeof VehicleTransactionForm>[0]['onSubmit'];
type SimplifiedFormSubmitHandler = Parameters<
  typeof VehicleSimplifiedTransactionForm
>[0]['onSubmit'];

interface VehicleEditCostDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  openSimplified?: boolean;
  costId: string;
  onSuccess?: (id: string) => void;
  onDiscard?: VoidFunction;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function VehicleEditCostForm(props: VehicleEditCostDialogProps) {
  const {
    data: cost,
    isLoading: isCostLoading,
    isError: hasCostError,
  } = useGetVehicleCostQuery({costId: props.costId, vehicleId: props.vehicleId});
  const [updateVehicleCost] = useUpdateVehicleCostMutation();
  const {isSaleActionEnabled} = useSaleVehicleActions(props.vehicleId);

  const {data: activeSaleVehicle} = useGetActiveSaleVehicleQuery({vehicleId: props.vehicleId});

  const {data: categories} = useListVehicleCostCategoriesQuery({});

  const {tenantCountry, tenantCurrency} = useTenant();

  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  const formRef = useSubscribeToFormChanges<VehicleTransactionFormRef>({
    watchFields: ['category'],
    onChange: (value, {formApi, formValues}) => {
      if (value.category?.[0] === 'FINANCE_AND_INSURANCE') {
        formApi?.setValue('expectedPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        formApi?.setValue('realPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        const expectedPrice = formValues.expectedPrice?.withoutVat?.amount;
        const realPrice = formValues.realPrice?.withoutVat?.amount;
        if (isNotNil(expectedPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: expectedPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('expectedPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
        if (isNotNil(realPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: realPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('realPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
      }
    },
  });

  const formRefSimplified = useSubscribeToFormChanges<VehicleSimplifiedTransactionFormRef>({
    watchFields: ['category'],
    onChange: (value, {formApi, formValues}) => {
      if (value.category?.[0] === 'FINANCE_AND_INSURANCE') {
        formApi?.setValue('expectedPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        const expectedPrice = formValues.expectedPrice?.withoutVat?.amount;
        if (isNotNil(expectedPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: expectedPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('expectedPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
      }
    },
  });

  const onSubmitFullForm: FullFormSubmitHandler = async (data) => {
    const {realPrice, expectedPrice, transactionType, calculationType, interestPA, ...rest} =
      data as DeepNonNullableWithPartial<typeof data>;

    const isOneTime = transactionType?.[0] === 'ONE_TIME' && realPrice;
    const isRecurring = transactionType?.[0] === 'RECURRING' && realPrice;
    const isSimpleCalculation = isRecurring && calculationType?.[0] === 'SIMPLE' && realPrice;
    const isCalculatedCalculation =
      isRecurring && calculationType?.[0] === 'CALCULATED' && realPrice;

    // Transform prices using helper functions
    const transformedExpectedPrice = transformToOneTimePrice(expectedPrice);
    const transformedRealPriceOneTime =
      !isRecurring && isOneTime ? transformToOneTimePrice(realPrice) : null;

    await updateVehicleCost({
      vehicleId: props.vehicleId,
      costId: props.costId,
      updateVehicleCostRequestBody: {
        ...rest,
        category: (rest.category?.[0] ?? null) as string | null,
        note: rest.note ?? null,
        expectedPrice: transformedExpectedPrice,
        realPriceOneTime: transformedRealPriceOneTime,
        realPriceRecurringCalculated: isCalculatedCalculation
          ? transformCalculatedRecurringPrice(
              realPrice,
              rest.startDate,
              rest.endDate,
              interestPA,
              tenantCurrency
            )
          : null,
        realPriceRecurringSimple: isSimpleCalculation
          ? transformSimpleRecurringPrice(
              data.realPrice!,
              rest.startDate,
              rest.endDate,
              tenantCurrency
            )
          : null,
      },
    })
      .unwrap()
      .then(() => {
        props.onSuccess?.(props.costId);
      })
      .catch(handleApiError);
  };

  const onSubmitSimplifiedForm: SimplifiedFormSubmitHandler = async (data) => {
    const transformedExpectedPrice = transformToOneTimePrice(data.expectedPrice);
    await updateVehicleCost({
      vehicleId: props.vehicleId,
      costId: props.costId,
      updateVehicleCostRequestBody: {
        name: data.name,
        category: (data.category?.[0] ?? null) as string | null,
        expectedPrice: transformedExpectedPrice,
        note: cost?.note ?? null,
        fileId: cost?.fileId ?? null,
        realPriceOneTime: isNotNil(cost?.realPriceOneTime?.withoutVat?.amount)
          ? cost?.realPriceOneTime
          : null,
        realPriceRecurringCalculated: cost?.realPriceRecurringCalculated ?? null,
        realPriceRecurringSimple: cost?.realPriceRecurringSimple ?? null,
      },
    })
      .unwrap()
      .then(() => {
        props.onSuccess?.(props.costId);
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isCostLoading} isError={hasCostError}>
      {props.openSimplified ? (
        <VehicleSimplifiedTransactionForm
          data-testid={props['data-testid']}
          canSetExpectedPrice={isSaleActionEnabled('SALE_VEHICLE_EDIT_EXPECTED_COST')}
          showCategories
          categories={categories ?? []}
          onSubmit={onSubmitSimplifiedForm}
          onDiscard={props.onDiscard}
          currency={tenantCurrency}
          countryCode={tenantCountry}
          values={{
            name: cost?.name ?? '',
            expectedPrice: cost?.expectedPrice
              ? {
                  ...cost.expectedPrice,
                  vatType: cost.expectedPrice.vatType ?? 'S',
                  withVat: {...cost.expectedPrice.withVat, currency: tenantCurrency},
                  withoutVat: {...cost.expectedPrice.withoutVat, currency: tenantCurrency},
                }
              : null,
            category: cost?.category ? [cost.category] : null,
          }}
          saleVehicle={activeSaleVehicle}
          ref={formRefSimplified}
        />
      ) : (
        <VehicleTransactionForm
          data-testid={props['data-testid']}
          canSetExpectedPrice={isSaleActionEnabled('SALE_VEHICLE_EDIT_EXPECTED_COST')}
          values={{
            ...cost,
            name: cost?.name ?? '',
            realPrice:
              parseRecurringSimplePrice(cost?.realPriceRecurringSimple) ??
              parseCalculatedRecurringPrice(cost?.realPriceRecurringCalculated) ??
              cost?.realPriceOneTime ??
              null,
            expectedPrice: cost?.expectedPrice ?? null,
            category: cost?.category ? [cost.category] : null,
            note: cost?.note ?? null,
            fileId: cost?.fileId ?? null,
            transactionType: getTransactionType(cost),
            calculationType: getCalculationType(cost),
            startDate:
              cost?.realPriceRecurringSimple?.startDate ??
              cost?.realPriceRecurringCalculated?.startDate ??
              null,
            endDate:
              cost?.realPriceRecurringSimple?.endDate ??
              cost?.realPriceRecurringCalculated?.endDate ??
              null,
            interestPA: toNumber(cost?.realPriceRecurringCalculated?.interestRate) ?? null,
          }}
          onSubmit={onSubmitFullForm}
          onDiscard={props.onDiscard}
          currency={tenantCurrency}
          countryCode={tenantCountry}
          showCategories
          categories={categories ?? []}
          soldAt={activeSaleVehicle?.soldAt}
          ref={formRef}
        />
      )}
    </DataStatus>
  );
}

function transformToOneTimePrice(price: VatCodePrice | null | undefined): OneTimePrice | null {
  if (!price || price.withoutVat?.amount === null) {
    return null;
  }

  if (!price.vatDirection || !price.vatRate || !price.vatType) {
    return null;
  }

  return {
    vatType: price.vatType,
    vatRate: price.vatRate,
    withoutVat: price.withoutVat,
    withVat: price.withVat,
    vatDirection: price.vatDirection,
  } as OneTimePrice;
}

function parseRecurringSimplePrice(
  data: GetVehicleCostApiResponse['realPriceRecurringSimple'] | undefined
) {
  if (!data) {
    return null;
  }
  return {
    withoutVat: {
      amount: data?.price.amount ?? null,
      currency: data?.price.currency ?? null,
    },
    withVat: {
      amount: data?.price.amount ?? null,
      currency: data?.price.currency ?? null,
    },
  };
}

function parseCalculatedRecurringPrice(
  data: GetVehicleCostApiResponse['realPriceRecurringCalculated'] | undefined
) {
  if (!data) {
    return null;
  }
  return {
    withoutVat: {
      amount: data?.basePrice.amount ?? null,
      currency: data?.basePrice.currency ?? null,
    },
    withVat: {
      amount: data?.basePrice.amount ?? null,
      currency: data?.basePrice.currency ?? null,
    },
  };
}

function transformSimpleRecurringPrice(
  vatCodePrice: VatCodePrice,
  startDate: string,
  endDate: string,
  currency: string
): RecurringPriceSimple {
  return {
    startDate,
    endDate,
    price: {
      amount: vatCodePrice.withoutVat.amount ?? '0',
      currency: vatCodePrice.withVat.currency ?? currency,
    },
  };
}

function transformCalculatedRecurringPrice(
  vatCodePrice: VatCodePrice,
  startDate: string,
  endDate: string,
  interestPA: number,
  currency: string
): RecurringPriceCalculated {
  return {
    startDate,
    endDate,
    basePrice: {
      amount: vatCodePrice.withoutVat.amount ?? '0',
      currency: vatCodePrice.withoutVat.currency ?? currency,
    },
    interestRate: String(interestPA),
  };
}
